import { Box, Grid } from "@mui/material";
import { Fragment, useCallback, useMemo, useState } from "react";
import ChangeSizeButton from "../../../../../components/ChangeSizeButton";
import TooltipWrapper from "../../../../../components/TooltipWrapper";
import cloneDeep from "../../../../../utilities/cloneDeep";
import {
  ConditionDescriptor,
  ConditionEqualityType,
  ConditionField,
  DimensionDescriptor,
  DimensionDictionary,
  ItemDataType,
} from "../../../../api/biClient.types";
import { useSharedReportingLocalization } from "../../../../hooks/useSharedReportingLocalization.ts";
import CustomLabel from "../fields/CustomLabel.tsx";
import FieldOptionDeleteButton from "../fields/FieldOptionDeleteButton.tsx";
import FieldOptionsDivider from "../fields/FieldOptionsDivider.tsx";
import FieldPopover from "../fields/FieldPopover.tsx";
import SwitchField from "../fields/SwitchField.tsx";
import DateFilter from "./DateFilter.tsx";
import TextFilterCondition from "./TextFilterCondition.tsx";
import { getCrossFilterDictionary, isCrossFilterToBeRefreshed } from "./conditionsHelper.tsx";

interface Props {
  anchorEl: HTMLElement;
  field: ConditionField;
  showParameter?: boolean;
  readonlyCustomLabel?: boolean;
  showOnlySelectedByDefault?: boolean;
  readonly?: boolean;
  canBeRemoved?: boolean;
  resizable?: boolean;
  dictionary: DimensionDictionary[];
  disableEqualitySelection?: boolean;
  updateParameter?: (field: ConditionField, newValue: boolean) => void;
  updateMandatory?: (field: ConditionField, newValue: boolean) => void;
  updateCustomLabel?: (customLabel: string | undefined) => void;
  save: (filter: ConditionDescriptor) => void;
  onRemoveItem?: (item: ConditionField) => void;
  close: () => void;
  CustomId: React.ReactNode;
}

export const ConditionFieldOptionPopup = ({
  anchorEl,
  field,
  showParameter,
  readonlyCustomLabel,
  showOnlySelectedByDefault,
  readonly,
  canBeRemoved,
  resizable,
  dictionary,
  disableEqualitySelection,
  updateParameter,
  updateMandatory,
  updateCustomLabel,
  save,
  onRemoveItem,
  close,
  CustomId,
}: Props) => {
  const { report: locale } = useSharedReportingLocalization();

  const [newFilter, setNewFilter] = useState<ConditionDescriptor>(() => {
    if (field.config.filter === undefined) {
      return {
        dimensionName: field.meta.name,
        values: [],
        equalityType: ConditionEqualityType.Equal,
      };
    }
    return cloneDeep(field.config.filter);
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [editing, setIsEditing] = useState(false);

  const meta = useMemo(() => {
    return { ...field.meta, dictionary: dictionary } as DimensionDescriptor;
  }, [dictionary, field.meta]);

  const onBeforeClose = useCallback(() => {
    if (isCrossFilterToBeRefreshed(field)) {
      close();
      return;
    }
    if (field.meta.type === ItemDataType.General) {
      const dictionary = getCrossFilterDictionary(field, meta || field.meta);
      newFilter.values = newFilter.values.filter((value) =>
        dictionary.some((row) => row[field.meta.keyFieldName] === value)
      );
    }
    save(newFilter);
  }, [field, meta, newFilter, save, close]);

  const updateAsParameter = useCallback(
    (useAsParameter: boolean) => {
      updateParameter?.call(this, field, useAsParameter);
    },
    [field, updateParameter]
  );

  const updateAsMandatory = useCallback(
    (useAsMandatory: boolean) => {
      updateMandatory?.call(this, field, useAsMandatory);
    },
    [field, updateMandatory]
  );

  const handleOnRemove = useCallback(() => {
    onRemoveItem?.call(null, field);
  }, [field, onRemoveItem]);

  return (
    <FieldPopover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={() => onBeforeClose()}
      sx={{ display: "flex", flexDirection: "column", p: 0, ...getSizeParameters(isExpanded) }}
    >
      <CustomLabel
        customFieldValue={field.config.customLabel}
        originalFieldValue={field.meta.caption}
        updateCustomFieldValue={updateCustomLabel}
        readonly={readonlyCustomLabel || false}
        onEditMode={setIsEditing}
        sx={{ pt: 1 }}
      >
        {resizable && !editing && (
          <TooltipWrapper key={+isExpanded} title={isExpanded ? "Collapse" : "Expand"} placement="bottom">
            <Box sx={{ display: "flex", marginLeft: "auto" }}>
              <ChangeSizeButton isExpanded={isExpanded} toggle={() => setIsExpanded((prev) => !prev)} />
            </Box>
          </TooltipWrapper>
        )}
      </CustomLabel>
      {CustomId}
      <FieldOptionsDivider />
      {field.meta.type === ItemDataType.Date ? (
        <DateFilter
          field={field}
          onFilterUpdated={setNewFilter}
          sx={{ width: "100%" }}
          disableEqualitySelection={disableEqualitySelection}
        />
      ) : (
        <Grid container sx={{ display: "flex", flexDirection: "column", width: "100%", flex: 1 }}>
          <Grid container sx={{ display: "flex", flex: 1, p: "1rem", width: "100%" }}>
            <TextFilterCondition
              field={field}
              meta={meta}
              readonly={readonly}
              showOnlySelectedByDefault={showOnlySelectedByDefault}
              onFilterUpdated={setNewFilter}
            />
          </Grid>
        </Grid>
      )}
      {showParameter && [
        <FieldOptionsDivider key="dividerTop" />,
        <Grid key="menu-items" container py={0.5} flexDirection={"column"} width={"100%"}>
          <SwitchField
            key="parameter"
            label="Set as Parameter"
            checked={field.config.parameter || false}
            tooltipText={locale.set_parameter_info}
            onChange={() => updateAsParameter(!field.config.parameter)}
            containerSx={{ px: 2, py: 0.75 }}
          />
          {field.config.parameter && (
            <SwitchField
              key="mandatory"
              label="Set as Mandatory"
              checked={field.config.mandatory || false}
              onChange={() => updateAsMandatory(!field.config.mandatory)}
              containerSx={{ px: 2, py: 0.75 }}
            />
          )}
        </Grid>,
      ]}
      {canBeRemoved && !readonly && (
        <Fragment key={"RemoveItem"}>
          <FieldOptionsDivider />
          <FieldOptionDeleteButton onRemove={handleOnRemove} />
        </Fragment>
      )}
    </FieldPopover>
  );
};

export default ConditionFieldOptionPopup;

const expandedSize = {
  width: "600px",
  height: "660px",
};
const collapsedSize = {
  width: "370px",
  height: "auto",
};

function getSizeParameters(isExpanded: boolean) {
  return isExpanded ? expandedSize : collapsedSize;
}
